import { useMemo, useState } from 'react';
import { fmtCcy, fmtDate } from '../domain';
import type { Quote, QuoteStatus, User } from '../types';

interface Props {
  user: User;
  quotes: Quote[];
  onAddQuote: () => void;
  onEdit: (q: Quote) => void;
  onSetStatus: (q: Quote, status: QuoteStatus) => void;
  onDelete: (q: Quote) => void;
}

type Tab = 'open' | 'won' | 'lost';

export function Quotes({ user, quotes, onAddQuote, onEdit, onSetStatus, onDelete }: Props) {
  const [tab, setTab] = useState<Tab>('open');

  // Server already scopes per-user customers, but admins get everyone.
  // For "my customers" experience we always filter to the user's own customer scope.
  const myQuotes = useMemo(() => (
    user.role === 'admin' ? quotes : quotes.filter(q => q.customerAmName === user.name)
  ), [quotes, user]);

  const open = myQuotes.filter(q => q.status === 'open');
  const won = myQuotes.filter(q => q.status === 'won');
  const lost = myQuotes.filter(q => q.status === 'lost');
  const totalRev = won.reduce((s, q) => s + (q.revenue || 0), 0);
  const decided = won.length + lost.length;
  const winRate = decided > 0 ? Math.round((won.length / decided) * 100) : 0;

  const visible = tab === 'open' ? open : tab === 'won' ? won : lost;

  return (
    <>
      <div className="section-hdr">
        <div className="section-hdr-title">Won &amp; Lost <span style={{ color: 'var(--accent)', fontStyle: 'italic' }}>Quotes</span></div>
        <button className="action-btn btn-note" onClick={onAddQuote}>+ New Quote</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 11, marginBottom: 18 }}>
        <SummaryCard val={open.length} col="var(--accent2)" label="Open" />
        <SummaryCard val={won.length} col="var(--call)" label="Won" />
        <SummaryCard val={lost.length} col="var(--danger)" label="Lost" />
        <SummaryCard val={fmtCcy(totalRev)} col="var(--accent)" label="Revenue Won" />
        <SummaryCard val={`${winRate}%`} col="var(--note)" label="Win Rate" />
      </div>

      <div className="inner-tabs">
        <button className={`itab ${tab === 'open' ? 'on' : ''}`} onClick={() => setTab('open')}>📝 Open ({open.length})</button>
        <button className={`itab ${tab === 'won' ? 'on' : ''}`} onClick={() => setTab('won')}>✅ Won ({won.length})</button>
        <button className={`itab ${tab === 'lost' ? 'on' : ''}`} onClick={() => setTab('lost')}>❌ Lost ({lost.length})</button>
      </div>

      <div className="mgmt-section">
        {visible.length === 0 ? (
          <p style={{ color: 'var(--text-dim)', fontSize: 13 }}>
            {tab === 'open' ? 'No open quotes. Hit + New Quote to log one.' : `No ${tab} quotes yet.`}
          </p>
        ) : (
          [...visible]
            .sort((a, b) => (tab === 'open' ? b.createdTs - a.createdTs : (b.decidedTs ?? 0) - (a.decidedTs ?? 0)))
            .map(q => (
              <QuoteRow
                key={q.id} q={q}
                onWin={() => onSetStatus(q, 'won')}
                onLose={() => onSetStatus(q, 'lost')}
                onReopen={() => onSetStatus(q, 'open')}
                onEdit={() => onEdit(q)}
                onDelete={() => onDelete(q)}
              />
            ))
        )}
      </div>
    </>
  );
}

interface QuoteRowProps {
  q: Quote;
  onWin: () => void;
  onLose: () => void;
  onReopen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

export function QuoteRow({ q, onWin, onLose, onReopen, onEdit, onDelete }: QuoteRowProps) {
  return (
    <div className="assign-row">
      <div className="assign-name" style={{ minWidth: 160 }}>
        <div style={{ fontWeight: 500 }}>{q.customerName}</div>
        <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>
          Ref <span style={{ fontFamily: "'DM Mono', monospace", color: 'var(--accent2)' }}>{q.clarityRef}</span>
          {q.decidedTs ? ` · decided ${fmtDate(q.decidedTs)}` : ` · created ${fmtDate(q.createdTs)}`}
        </div>
      </div>
      <QuoteStatusBadge status={q.status} />
      {q.notes && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1, minWidth: 120 }}>
          {q.notes.slice(0, 80)}{q.notes.length > 80 ? '…' : ''}
        </div>
      )}
      <div style={{ fontSize: 12, fontWeight: 600, color: q.revenue ? 'var(--call)' : 'var(--text-dim)' }}>
        {q.revenue ? fmtCcy(q.revenue) : '£0'}
      </div>
      <QuoteRowActions q={q} onWin={onWin} onLose={onLose} onReopen={onReopen} onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}

export function QuoteRowActions({ q, onWin, onLose, onReopen, onEdit, onDelete }: QuoteRowProps) {
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
      {q.status !== 'won' && (
        <button className="entry-del-btn" style={{ color: 'var(--call)' }} onClick={onWin}>✓ Win</button>
      )}
      {q.status !== 'lost' && (
        <button className="entry-del-btn" style={{ color: 'var(--danger)' }} onClick={onLose}>✕ Lose</button>
      )}
      {q.status !== 'open' && (
        <button className="entry-del-btn" onClick={onReopen}>↺ Reopen</button>
      )}
      <button className="entry-del-btn" onClick={onEdit}>Edit</button>
      <button className="entry-del-btn" onClick={onDelete}>Delete</button>
    </div>
  );
}

function SummaryCard({ val, col, label }: { val: number | string; col: string; label: string }) {
  const isLong = typeof val === 'string' && val.length > 6;
  return (
    <div className="mgmt-section" style={{ textAlign: 'center' }}>
      <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: isLong ? 28 : 36, color: col }}>{val}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
    </div>
  );
}

export function QuoteStatusBadge({ status }: { status: QuoteStatus }) {
  const map: Record<QuoteStatus, [string, string]> = {
    open: ['ps-notstarted', '○ Open'],
    won:  ['ps-won', '✅ Won'],
    lost: ['ps-lost', '❌ Lost']
  };
  const [cls, lbl] = map[status];
  return <span className={`ps-badge ${cls}`}>{lbl}</span>;
}
